// =============================================================================
// features/configuracoes/comercial/DistributionSection.tsx — Grupo
// "Distribuição" (doc 26 §6.9): estratégias habilitadas + modo dry-run.
// =============================================================================

import type { AssignmentStrategy } from '@elemento/shared-schemas';
import * as React from 'react';

import { SettingsCard } from './SettingsCard';
import { Toggle } from './Toggle';
import type { DraftErrors, SettingsDraft } from './types';

const STRATEGIES: { value: AssignmentStrategy; label: string; description: string }[] = [
  {
    value: 'fixed_owner',
    label: 'Vendedor fixo',
    description: 'Cada lead pertence a um vendedor durante toda a campanha.',
  },
  {
    value: 'round_robin',
    label: 'Round-robin',
    description: 'Distribuição equilibrada e previsível entre os vendedores.',
  },
  {
    value: 'shared_queue',
    label: 'Fila compartilhada',
    description: 'Vendedores puxam o próximo lead disponível de uma fila comum.',
  },
];

interface DistributionSectionProps {
  draft: SettingsDraft;
  errors: DraftErrors;
  isDefault: boolean;
  onChange: (updates: Partial<SettingsDraft>) => void;
  disabled?: boolean;
}

export function DistributionSection({
  draft,
  errors,
  isDefault,
  onChange,
  disabled = false,
}: DistributionSectionProps): React.JSX.Element {
  const toggleStrategy = (strategy: AssignmentStrategy, checked: boolean): void => {
    const enabledAssignmentStrategies = checked
      ? [...draft.enabledAssignmentStrategies, strategy]
      : draft.enabledAssignmentStrategies.filter((s) => s !== strategy);
    onChange({ enabledAssignmentStrategies });
  };

  return (
    <SettingsCard
      title="Distribuição"
      description="Estratégias de atribuição disponíveis para novas campanhas e o modo de simulação do distribuidor."
      isDefault={isDefault}
    >
      <div className="flex flex-col gap-3">
        {STRATEGIES.map((strategy) => (
          <Toggle
            key={strategy.value}
            id={`comercial-strategy-${strategy.value}`}
            checked={draft.enabledAssignmentStrategies.includes(strategy.value)}
            onChange={(checked) => toggleStrategy(strategy.value, checked)}
            label={strategy.label}
            description={strategy.description}
            disabled={disabled}
          />
        ))}
        {errors.enabledAssignmentStrategies && (
          <span role="alert" className="text-xs text-danger">
            {errors.enabledAssignmentStrategies}
          </span>
        )}
      </div>

      <div className="pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Toggle
          id="comercial-dry-run"
          checked={draft.distributorDryRun}
          onChange={(checked) => onChange({ distributorDryRun: checked })}
          label="Modo de simulação (dry-run)"
          description={
            draft.distributorDryRun
              ? 'Ligado — a distribuição é simulada, nada é atribuído ou disparado de verdade.'
              : 'Desligado — a distribuição está VALENDO: leads são atribuídos e contatados de verdade.'
          }
          disabled={disabled}
        />
      </div>
    </SettingsCard>
  );
}
